import { Link } from "react-router-dom";
import { useMemo, useState } from "react";
import { Lead } from "../types/lead";
import { Input } from "./ui/input";
import { Search, SearchX } from "lucide-react";
import { AvailableLeadCard } from "../pages/Projects/AvailableLeadCard";
import "../pages/Projects/available-leads.css";

interface LeadsProProps {
  leads: Lead[];
  onViewDetails: (leadId: string) => void;
  onApplyLead?: (leadId: string) => void;
  onDeclineLead?: (leadId: string) => void;
  canRespondToJobs?: boolean;
  canAcceptJobs?: boolean;
  setShowPlanModal?: (show: boolean) => void;
  requested?: boolean;
}

export function LeadsPro({ leads, onViewDetails, onApplyLead, onDeclineLead,
  canRespondToJobs = false, canAcceptJobs = false, requested = false }: LeadsProProps) {
  const fieldPrefix = requested ? "requested-leads" : "available-leads";
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [sortBy, setSortBy] = useState("date");
  const categories = useMemo(() => Array.from(new Set(leads.map(lead =>
    lead.category?.trim().toLowerCase()).filter(Boolean))).sort(), [leads]);
  const hasFilters = Boolean(searchQuery || selectedCategory !== "all" || sortBy !== "date");
  const resetFilters = () => { setSearchQuery(""); setSelectedCategory("all"); setSortBy("date"); };
  const sortedLeads = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return leads.filter(lead => {
      const matchesSearch = [lead.title, lead.description, lead.location?.city, lead.location?.state]
        .some(value => value?.toLowerCase().includes(query));
      return matchesSearch && (selectedCategory === "all" || lead.category?.trim().toLowerCase() === selectedCategory);
    }).sort((a, b) => {
      if (sortBy === "budget") return Number(b.budget) - Number(a.budget);
      if (sortBy === "budget-low") return Number(a.budget) - Number(b.budget);
      return new Date((requested && b.requestedDate) || b.postedDate).getTime() -
        new Date((requested && a.requestedDate) || a.postedDate).getTime();
    });
  }, [leads, searchQuery, selectedCategory, sortBy, requested]);

  return <section className="lead-management-content available-leads" aria-label={requested ? "Requested leads" : "Available leads"}>
    <div className="available-leads-toolbar">
      <div className="available-leads-search">
        <label htmlFor={`${fieldPrefix}-search`}>Search leads</label>
        <div className="app-search-field">
          <Search className="app-field-icon" aria-hidden="true" />
          <Input id={`${fieldPrefix}-search`} type="search" placeholder="Search title or location…"
            value={searchQuery} onChange={event => setSearchQuery(event.target.value)} />
        </div>
      </div>
      <div><label htmlFor={`${fieldPrefix}-category`}>Category</label>
        <select id={`${fieldPrefix}-category`} value={selectedCategory} onChange={event => setSelectedCategory(event.target.value)}>
          <option value="all">All categories</option>
          {categories.map(category => <option key={category} value={category}>{category.charAt(0).toUpperCase() + category.slice(1)}</option>)}
        </select>
      </div>
      <div><label htmlFor={`${fieldPrefix}-sort`}>Sort by</label>
        <select id={`${fieldPrefix}-sort`} value={sortBy} onChange={event => setSortBy(event.target.value)}>
          <option value="date">Newest first</option><option value="budget">Highest budget</option>
          {requested && <option value="budget-low">Lowest budget</option>}
        </select>
      </div>
    </div>
    <div className="available-leads-results">
      <p role="status">{sortedLeads.length} of {leads.length} {requested ? "requested" : "available"} {leads.length === 1 ? "lead" : "leads"}</p>
      {hasFilters && <button type="button" className="available-lead-button" onClick={resetFilters}>Reset filters</button>}
    </div>
    {(!canRespondToJobs || !canAcceptJobs) && <p className="available-leads-notice">
      {!canRespondToJobs ? "You can view lead details. Contact your account administrator for access to accept or decline leads." :
        "You can view and decline leads. Billing access is required to accept a lead."}
    </p>}
    {sortedLeads.length ? <div className="available-leads-list">
      {sortedLeads.map(lead => <AvailableLeadCard key={lead.id} lead={lead} requested={requested} onViewDetails={onViewDetails}
        onApplyLead={canAcceptJobs ? onApplyLead : undefined} onDeclineLead={canRespondToJobs ? onDeclineLead : undefined} />)}
    </div> : <div className="available-leads-empty">
      <SearchX size={28} aria-hidden="true" />
      <h2>{leads.length ? "No matching leads" : (requested ? "No requested leads yet" : "No leads available yet")}</h2>
      <p>{leads.length ? "Try another title, location or category." : (requested ? "Browse available leads to find your next project." : "New opportunities will appear here when they become available.")}</p>
      {hasFilters && <button type="button" className="available-lead-button" onClick={resetFilters}>Clear filters</button>}
      {requested && !leads.length && <Link className="available-lead-button available-lead-button-primary" to="/pages-projects">Browse leads</Link>}
    </div>}
  </section>;
}
